import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { formatApiError } from "../lib/api";

export function GoogleButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="google-auth-button"
      className="flex w-full items-center justify-center gap-3 rounded-full border-2 border-slate-900 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]"
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {label}
    </button>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleAuth(pendingRole) {
  if (pendingRole) localStorage.setItem("gls_pending_role", pendingRole);
  const redirectUrl = window.location.origin + "/auth-callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function routeForRole(role) {
  if (!role) return "/choose-role";
  if (role === "candidate") return "/candidate";
  if (role === "admin") return "/admin";
  return "/company";
}

export default function Login() {
  const { t, lang } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password, lang);
      navigate(routeForRole(user.role));
    } catch (err) {
      const msg = formatApiError(err, t("auth.loginError"));
      toast.error(msg === "ACCOUNT_BLOCKED" ? t("auth.accountBlocked") : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="login-page">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-4 py-16">
        <div className="rounded-md border-2 border-slate-900 bg-white p-8 shadow-[8px_8px_0px_0px_rgba(56,189,248,1)]">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{t("auth.loginTitle")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auth.loginSubtitle")}</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("auth.email")}</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
                <input
                  type="email" required value={form.email}
                  data-testid="login-email-input"
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-md border-2 border-slate-900 bg-white py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("auth.password")}</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
                <input
                  type={showPassword ? "text" : "password"} required value={form.password}
                  data-testid="login-password-input"
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full rounded-md border-2 border-slate-900 bg-white py-2.5 pl-10 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                  aria-label="Afficher le mot de passe"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-900"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={2} /> : <Eye className="h-4 w-4" strokeWidth={2} />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-sky-600 hover:underline">
                  {t("auth.forgotLink")}
                </Link>
              </div>
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.loginBtn")}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-300" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("auth.orContinueWith")}</span>
            <div className="h-px flex-1 bg-slate-300" />
          </div>

          <GoogleButton onClick={() => startGoogleAuth()} label={t("auth.google")} />

          <p className="mt-6 text-center text-sm text-slate-500">
            {t("auth.noAccount")}{" "}
            <Link to="/register" data-testid="goto-register-link" className="font-bold text-sky-600 hover:underline">
              {t("nav.register")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
